import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { usePageMeta } from "@/components/Share";

const KINDS = { all: "All", beach: "Beaches", club: "Clubs", retreat: "Retreats", other: "Other" };

function LocationCard({ l }) {
  return (
    <Link to={`/locations/${l.slug}`} className="group grid sm:grid-cols-5 gap-6 py-8 border-t border-[#e2dcd4] img-zoom" data-testid="location-card-item">
      <div className="sm:col-span-2 overflow-hidden aspect-[4/3] bg-[#f4f0ea]">
        {l.cover_image && <img src={imgUrl(l.cover_image)} alt={l.title} className="w-full h-full object-cover" loading="lazy" />}
      </div>
      <div className="sm:col-span-3 flex flex-col">
        <p className="eyebrow">{[l.region, KINDS[l.kind] || l.kind].filter(Boolean).join(" · ")}</p>
        <h3 className="mt-2 text-2xl sm:text-3xl text-[#1a261f] group-hover:text-[#1f402b] flex items-start justify-between gap-4">
          {l.title} <ArrowUpRight size={18} className="mt-2 text-[#c5903b] shrink-0" />
        </h3>
        <p className="mt-3 text-[#5a665e] leading-relaxed">{l.summary}</p>
        {l.status && <span className="mt-4 self-start font-mono-k text-[10px] uppercase tracking-widest px-2 py-1 bg-[#eef3ee] text-[#1f402b]">{l.status}</span>}
      </div>
    </Link>
  );
}

export default function Locations() {
  const [items, setItems] = useState(null);
  const [kind, setKind] = useState("all");
  usePageMeta("Locations", "Notes on clothing-optional beaches, clubs and retreats around Aotearoa.");
  useEffect(() => {
    api.get("/public/locations").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);
  const shown = (items || []).filter((l) => kind === "all" || l.kind === kind);

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20" data-testid="locations-page">
      <p className="eyebrow rise">Locations</p>
      <h1 className="rise d1 mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#1a261f] max-w-3xl">Where to go, and what to know.</h1>
      <p className="rise d2 mt-5 text-[#5a665e] max-w-xl leading-relaxed">Practical notes on beaches, clubs and retreats — each with live weather and UV. Always check local signage; status can change.</p>
      <div className="mt-10 flex flex-wrap gap-2" data-testid="locations-filter">
        {Object.entries(KINDS).map(([k, v]) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`locations-filter-${k}`}
            className={`px-4 py-1.5 text-sm border transition-colors ${kind === k ? "bg-[#1f402b] text-white border-[#1f402b]" : "border-[#e2dcd4] text-[#3c4a41] hover:border-[#1f402b]"}`}>
            {v}
          </button>
        ))}
      </div>
      <div className="mt-8">
        {items === null ? <p className="text-sm text-[#5a665e]">Loading…</p>
          : shown.length === 0 ? <p className="text-[#5a665e] py-8 border-t border-[#e2dcd4]" data-testid="locations-empty">No locations listed here yet.</p>
          : shown.map((l) => <LocationCard key={l.id} l={l} />)}
      </div>
    </div>
  );
}
